import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Chip, Fade, CircularProgress
} from '@mui/material';
import jsPDF from 'jspdf';

const DoctorEntryIndex = () => {
  const history = useHistory();
  const { isAuthenticated, companyId, token, logout } = useAuth();
  const getCurrentDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  const [doctorEntries, setDoctorEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [mounted, setMounted] = useState(false);
  const [banner, setBanner] = useState({ show: false, message: '', type: '' });
  const [fromDate, setFromDate] = useState(getCurrentDate());
  const [toDate, setToDate] = useState(getCurrentDate());
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);

  // Fetch company details for UPI QR code
  const fetchCompanyDetails = async () => {
    try {
      const response = await fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token);
      if (response.responseCode === '1111') {
        setCompanyDetails(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  };

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
    fetchDoctorEntries();
    fetchCompanyDetails();
  }, [isAuthenticated, companyId, token, logout]);

  useEffect(() => {
    const filtered = doctorEntries.filter(entry =>
      entry.PatientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.DoctorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.MRDNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.ChiefComplaint?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredEntries(filtered);
  }, [searchQuery, doctorEntries]);

  const showBanner = (message, type = 'info') => {
    setBanner({ show: true, message, type });
    setTimeout(() => {
      setBanner({ show: false, message: '', type: '' });
    }, 4000);
  };

  const fetchDoctorEntries = async () => {
    setLoading(true);
    try {
      let endpoint = `/${companyId}`;
      if (fromDate && toDate) {
        endpoint = `/${companyId}/${fromDate}/${toDate}`;
      }
      const response = await fetchData('DoctorEntryApi', endpoint, token);
      if (response.responseCode === '1111') {
        setDoctorEntries(response.response_data || []);
        setFilteredEntries(response.response_data || []);
      }
    } catch (error) {
      console.error('Error fetching doctor entries:', error);
      showBanner('Failed to load doctor entries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    showBanner('Refreshing data...', 'info');
    fetchDoctorEntries();
  };

  const handleDelete = (entry) => {
    setEntryToDelete(entry);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!entryToDelete) return;

    setDeleting(true);
    try {
      const response = await deleteData('DoctorEntryApi', `${entryToDelete.Id}`, token);
      if (response.responseCode === '1111') {
        showBanner('Doctor entry deleted successfully', 'success');
        setDeleteModalOpen(false);
        setEntryToDelete(null);
        fetchDoctorEntries();
      } else {
        showBanner('Failed to delete doctor entry', 'error');
      }
    } catch (error) {
      console.error('Error deleting doctor entry:', error);
      showBanner('Failed to delete doctor entry', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setEntryToDelete(null);
  };

  const handleView = async (id) => {
    setLoadingDetails(true);
    setViewModalOpen(true);
    try {
      const response = await fetchData('DoctorEntryApi', `/${id}`, token);
      if (response.responseCode === '1111') {
        setSelectedEntry(response.response_data);
      } else {
        showBanner('Failed to load entry details', 'error');
        setViewModalOpen(false);
      }
    } catch (error) {
      console.error('Error fetching entry details:', error);
      showBanner('Failed to load entry details', 'error');
      setViewModalOpen(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedEntry(null);
  };

  const handleEdit = (id) => {
    history.push(`/DoctorEntryEdit/${id}`);
  };

  const handlePrint1 = async (id) => {
    setLoadingDetails(true);
    try {
      const response = await fetchData('DoctorEntryApi', `/${id}`, token);
      if (response.responseCode === '1111') {
        const entry = response.response_data;

        // Create print content
        const printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Prescription - ${entry.DoctorEntry?.PatientName || 'Patient'}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
              .header { text-align: center; padding: 15px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border-radius: 8px; margin-bottom: 20px; }
              .header h1 { font-size: 22px; margin-bottom: 5px; }
              .header p { font-size: 12px; opacity: 0.9; }
              .patient-info { background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 20px; border: 1px solid #e2e8f0; }
              .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
              .info-item label { font-size: 10px; color: #64748b; text-transform: uppercase; font-weight: 700; display: block; margin-bottom: 3px; }
              .info-item span { font-size: 13px; font-weight: 600; color: #0f172a; }
              .section { margin-bottom: 20px; }
              .section-title { font-size: 14px; font-weight: 700; color: #667eea; padding: 8px 12px; background: #eef2ff; border-radius: 6px; margin-bottom: 10px; display: flex; align-items: center; gap: 8px; }
              table { width: 100%; border-collapse: collapse; }
              th { background: #1e293b; color: #f1f5f9; padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; }
              td { padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
              tr:nth-child(even) { background: #f8fafc; }
              .footer { text-align: center; padding-top: 20px; border-top: 1px solid #e2e8f0; margin-top: 20px; font-size: 11px; color: #6b7280; }
              .complaint-box { background: #fff7ed; padding: 12px; border-radius: 6px; border-left: 4px solid #f97316; margin-bottom: 15px; }
              .complaint-box label { font-size: 10px; color: #9a3412; text-transform: uppercase; font-weight: 700; }
              .complaint-box p { font-size: 13px; color: #374151; margin-top: 5px; }
              @media print {
                body { padding: 10px; }
                .header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Prescription Details</h1>
              <p>Medical Record</p>
            </div>
            
            <div class="patient-info">
              <div class="info-grid">
                <div class="info-item">
                  <label>Patient Name</label>
                  <span>${entry.DoctorEntry?.PatientName || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <label>MRD No</label>
                  <span style="color: #667eea;">${entry.DoctorEntry?.MRDNo || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <label>Phone</label>
                  <span>${entry.DoctorEntry?.ContactNumber1 || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <label>Doctor</label>
                  <span>${entry.DoctorEntry?.DoctorName || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <label>Entry Date</label>
                  <span>${formatDate(entry.DoctorEntry?.DoctorEntryDate)}</span>
                </div>
              </div>
            </div>
            
            ${entry.DoctorEntry?.ChiefComplaint ? `
              <div class="complaint-box">
                <label>Chief Complaint</label>
                <p>${entry.DoctorEntry.ChiefComplaint}</p>
              </div>
            ` : ''}
            
            ${entry.DoctorEntry?.Diagnosis ? `
              <div class="complaint-box" style="background: #f0fdf4; border-left-color: #22c55e;">
                <label style="color: #166534;">Diagnosis</label>
                <p>${entry.DoctorEntry.Diagnosis}</p>
              </div>
            ` : ''}
            
            ${entry.ObservationDetails && entry.ObservationDetails.length > 0 ? `
              <div class="section">
                <div class="section-title">🩺 Observations</div>
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Value</th>
                      <th>Unit</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${entry.ObservationDetails.map(obs => `
                      <tr>
                        <td>${obs.ObservationType || 'N/A'}</td>
                        <td style="font-weight: 700;">${obs.ObservationValue || 'N/A'}</td>
                        <td>${obs.Unit || '-'}</td>
                        <td>${obs.Remarks || '-'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            ` : ''}
            
            ${entry.PrescriptionDetails && entry.PrescriptionDetails.length > 0 ? `
              <div class="section">
                <div class="section-title" style="background: #fef3c7; color: #92400e;">💊 Prescriptions</div>
                <table>
                  <thead>
                    <tr>
                      <th>Medicine</th>
                      <th>Dosage</th>
                      <th>Frequency</th>
                      <th>Duration</th>
                      <th>Instructions</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${entry.PrescriptionDetails.map(pres => `
                      <tr>
                        <td style="font-weight: 700;">${pres.MedicineName || 'N/A'}</td>
                        <td>${pres.Dosage || '-'}</td>
                        <td>${pres.Frequency || '-'}</td>
                        <td>${pres.Duration || '-'}</td>
                        <td>${pres.Instructions || '-'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            ` : ''}
            
            ${entry.TreatmentDetails && entry.TreatmentDetails.length > 0 ? `
              <div class="section">
                <div class="section-title" style="background: #d1fae5; color: #166534;">🏥 Treatments</div>
                <table>
                  <thead>
                    <tr>
                      <th>Treatment</th>
                      <th>Session</th>
                      <th>Description</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${entry.TreatmentDetails.map(treat => `
                      <tr>
                        <td style="font-weight: 700;">${treat.TreatmentName || 'N/A'}</td>
                        <td>${treat.SessionName || '-'}</td>
                        <td>${treat.Description || '-'}</td>
                        <td>${formatDate(treat.StartDate)}</td>
                        <td>${formatDate(treat.EndDate)}</td>
                        <td style="color: #059669; font-weight: 700;">${treat.Rate || '0'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            ` : ''}
            
            <div class="footer">
              <p>Generated on: ${new Date().toLocaleString()}</p>
              <p style="margin-top: 5px;">MediCare System</p>
            </div>
          </body>
          </html>
        `;

        // Open print window
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
          }, 250);
        } else {
          showBanner('Please allow popups to print', 'error');
        }
      } else {
        showBanner('Failed to load entry details for printing', 'error');
      }
    } catch (error) {
      console.error('Error fetching entry details for print:', error);
      showBanner('Failed to load entry details for printing', 'error');
    } finally {
      setLoadingDetails(false);
    }
  };


  const handlePrint = async (id) => {
    setLoadingDetails(true);
    try {
      // Log company details for debugging
      console.log('Company Details for QR:', companyDetails);
      console.log('PaymentUPIId:', companyDetails?.PaymentUPIId);
      
      const response = await fetchData('DoctorEntryApi', `/DoctorEntriesPrint/${id}`, token);
      if (response.responseCode === '1111') {
        const entry = response.response_data;
        const printData = entry.DoctorEntryPrint;
        const items = entry.DoctorEntryPrintItems || [];
        
        // Debug: Log printData to see available fields
        console.log('Print Data:', printData);
        console.log('DoctorEntryDate:', printData?.DoctorEntryDate);
        
        // Get UPI details from companyDetails
        const upiId = companyDetails?.PaymentUPIId || '';
        const upiName = companyDetails?.PaymentUPIName || printData?.CompanyName || 'Hospital';
        const totalAmount = printData?.GrandTotal || printData?.TotalAmount || 0;

        const formatBillDate = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        };

        const printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Invoice - ${printData?.PatientName || 'Patient'}</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
              * { margin: 0; padding: 0; box-sizing: border-box; }
              @page { size: A4; margin: 15mm; }
              body { font-family: 'Inter', Arial, sans-serif; color: #1f2937; font-size: 12px; background: white; line-height: 1.4; }
              .invoice { width: 100%; max-width: 210mm; margin: 0 auto; padding: 20px; }
              
              /* Header */
              .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 15px; border-bottom: 3px solid #1e3a8a; margin-bottom: 20px; }
              .company-info h1 { font-size: 24px; font-weight: 700; color: #1e3a8a; margin-bottom: 5px; }
              .company-info p { font-size: 11px; color: #4b5563; margin-bottom: 2px; }
              .invoice-title { text-align: right; }
              .invoice-title h2 { font-size: 28px; font-weight: 700; color: #1e3a8a; letter-spacing: 2px; }
              .invoice-title .bill-no { font-size: 12px; color: #6b7280; margin-top: 5px; }
              
              /* Info Section */
              .info-section { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 25px; }
              .info-box { padding: 15px; background: #f8fafc; border-radius: 8px; border-left: 4px solid #1e3a8a; }
              .info-box h3 { font-size: 11px; font-weight: 700; color: #1e3a8a; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #e5e7eb; }
              .info-row { display: flex; margin-bottom: 6px; }
              .info-row .label { width: 100px; font-size: 11px; color: #6b7280; }
              .info-row .value { font-size: 12px; font-weight: 600; color: #1f2937; }
              .patient-name { font-size: 16px; font-weight: 700; color: #1f2937; margin-bottom: 8px; }
              
              /* Items Table */
              .items-section { margin-bottom: 15px; }
              .items-section h3 { font-size: 10px; font-weight: 700; color: #1e3a8a; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
              .items-table { width: 100%; border-collapse: collapse; }
              .items-table thead { background: #1e3a8a; }
              .items-table th { padding: 6px 6px; text-align: left; font-size: 9px; font-weight: 600; color: white; text-transform: uppercase; }
              .items-table th:nth-child(1) { width: 6%; text-align: center; }
              .items-table th:nth-child(2) { width: 48%; }
              .items-table th:nth-child(3) { width: 10%; text-align: center; }
              .items-table th:nth-child(4) { width: 17%; text-align: right; }
              .items-table th:nth-child(5) { width: 19%; text-align: right; }
              .items-table tbody tr { border-bottom: 1px solid #e5e7eb; }
              .items-table tbody tr:nth-child(even) { background: #f9fafb; }
              .items-table td { padding: 5px 6px; font-size: 10px; }
              .items-table td:nth-child(1) { text-align: center; color: #6b7280; }
              .items-table td:nth-child(3) { text-align: center; }
              .items-table td:nth-child(4), .items-table td:nth-child(5) { text-align: right; }
              .items-table td:nth-child(5) { font-weight: 600; color: #059669; }
              .treatment-name { font-weight: 600; color: #1f2937; font-size: 10px; }
              .session-name { font-size: 8px; color: #6b7280; margin-top: 1px; }
              
              /* Summary Section */
              .summary-section { display: grid; grid-template-columns: 1.2fr 1fr; gap: 15px; margin-bottom: 15px; }
              .payment-info { padding: 10px; background: #f8fafc; border-radius: 6px; }
              .payment-info h3 { font-size: 9px; font-weight: 700; color: #1e3a8a; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
              .payment-row { display: flex; align-items: center; margin-bottom: 5px; }
              .payment-row .label { width: 100px; font-size: 9px; color: #6b7280; }
              .payment-row .value { font-size: 10px; font-weight: 600; }
              .payment-badge { display: inline-block; background: #dcfce7; color: #166534; padding: 3px 8px; border-radius: 10px; font-size: 9px; font-weight: 600; }
              
              .totals-box { padding: 8px; background: #f0fdf4; border-radius: 4px; border: 1px solid #86efac; height: fit-content; align-self: start; }
              .total-row { display: flex; justify-content: space-between; padding: 2px 0; font-size: 9px; }
              .total-row .label { color: #6b7280; }
              .total-row .value { font-weight: 600; }
              .grand-total { margin-top: 4px; padding-top: 4px; border-top: 1px solid #059669; }
              .grand-total .label { font-size: 10px; font-weight: 700; color: #1f2937; }
              .grand-total .value { font-size: 12px; font-weight: 700; color: #059669; }
              
              /* QR Section */
              .qr-section { display: flex; align-items: center; gap: 10px; margin-top: 8px; padding: 8px; background: white; border-radius: 6px; border: 1px dashed #d1d5db; }
              .qr-code img { width: 50px; height: 50px; }
              .qr-info { font-size: 8px; color: #6b7280; }
              .qr-info .title { font-weight: 700; color: #1e3a8a; margin-bottom: 2px; font-size: 8px; }
              
              /* Footer */
              .footer-section { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
              .terms { font-size: 10px; color: #6b7280; }
              .terms h4 { font-size: 10px; font-weight: 700; color: #374151; margin-bottom: 5px; }
              .signature-box { text-align: right; }
              .signature-line { width: 180px; height: 50px; border-bottom: 2px solid #1f2937; margin-left: auto; margin-bottom: 5px; }
              .signature-label { font-size: 11px; color: #374151; font-weight: 600; }
              
              .print-footer { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; }
              
              @media print {
                body { background: white; }
                .invoice { padding: 0; }
                .header, .items-table thead, .payment-badge, .totals-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="invoice">
              <!-- Header -->
              <div class="header">
                <div class="company-info">
                  <h1>${printData?.CompanyName || 'Hospital Name'}</h1>
                  <p>${printData?.CompanyAddressLine || ''}</p>
                  ${printData?.CompanyContactNumber1 ? `<p>📞 ${printData.CompanyContactNumber1}${printData?.CompanyEmail ? ' | ✉️ ' + printData.CompanyEmail : ''}</p>` : ''}
                  ${printData?.CompanyGSTNumber ? `<p>GSTIN: ${printData.CompanyGSTNumber}</p>` : ''}
                </div>
                <div class="invoice-title">
                  <h2>INVOICE</h2>
                  ${printData?.BillNumber ? `<div class="bill-no">Bill No: ${printData.BillPrefix || ''}${printData.BillNumber}</div>` : ''}
                  <div class="bill-no">Date: ${formatBillDate(printData?.BillDate)}</div>
                </div>
              </div>
              
              <!-- Patient & Doctor Info -->
              <div class="info-section">
                <div class="info-box">
                  <h3>Patient Information</h3>
                  <div class="patient-name">${printData?.PatientName || 'N/A'}</div>
                  <div class="info-row"><span class="label">MRD No:</span><span class="value">${printData?.PatientMRDNumber || 'N/A'}</span></div>
                  <div class="info-row"><span class="label">Contact:</span><span class="value">${printData?.PatientContactNumber || 'N/A'}</span></div>
                  ${printData?.PatientAddress ? `<div class="info-row"><span class="label">Address:</span><span class="value">${printData.PatientAddress}</span></div>` : ''}
                </div>
                <div class="info-box">
                  <h3>Consultation Details</h3>
                  ${printData?.DoctorName ? `<div class="info-row"><span class="label">Doctor:</span><span class="value">${printData.DoctorName}</span></div>` : ''}
                  <div class="info-row"><span class="label">Date:</span><span class="value">${formatBillDate(printData?.BillDate)}</span></div>
                  <div class="info-row"><span class="label">Time:</span><span class="value">${printData?.DoctorEntryDate ? new Date(printData.DoctorEntryDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}</span></div>
                </div>
              </div>
              
              <!-- Items Table -->
              <div class="items-section">
                <h3>Treatment / Services</h3>
                <table class="items-table">
                  <thead>
                    <tr>
                      <th style{{padding :'3px'}}>Sl.</th>
                      <th style{{padding :'3px'}}>Description</th>
                      <th style{{padding :'3px'}}>Qty</th>
                      <th style{{padding :'3px'}}>Rate</th>
                      <th style{{padding :'3px'}}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${items.map((item, index) => `
                      <tr>
                        <td>${index + 1}</td>
                        <td>
                          <div class="treatment-name">${item.TreatmentName || 'Consultation'}</div>
                          ${item.Session ? `<div class="session-name">${item.Session}</div>` : ''}
                        </td>
                        <td>${item.Quantity || 1}</td>
                        <td>₹${(item.Rate || printData?.ConsultationFee || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                        <td>₹${(item.Amount || item.Rate || printData?.ConsultationFee || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
              
              <!-- Summary Section -->
              <div class="summary-section">
                <div class="payment-info">
                  <h3>Payment Information</h3>
                  <div class="payment-row">
                    <span class="label">Payment Mode:</span>
                    <span class="payment-badge">${printData?.PaymentMode || 'Cash'}</span>
                  </div>
                  ${upiId ? `
                  <div class="qr-section">
                    <div class="qr-code">
                      <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent('upi://pay?pa=' + upiId + '&pn=' + encodeURIComponent(upiName) + '&am=' + totalAmount + '&cu=INR')}" alt="UPI QR" />
                    </div>
                    <div class="qr-info">
                      <div class="title">Scan to Pay via UPI</div>
                      <div>${upiId}</div>
                    </div>
                  </div>
                  ` : ''}
                </div>
                <div class="totals-box">
                  ${printData?.TaxableAmount ? `<div class="total-row"><span class="label">Subtotal</span><span class="value">₹${printData.TaxableAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span></div>` : ''}
                  ${printData?.CGSTAmount ? `<div class="total-row"><span class="label">CGST</span><span class="value">₹${printData.CGSTAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span></div>` : ''}
                  ${printData?.SGSTAmount ? `<div class="total-row"><span class="label">SGST</span><span class="value">₹${printData.SGSTAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span></div>` : ''}
                  ${printData?.DiscountAmount ? `<div class="total-row"><span class="label">Discount</span><span class="value" style="color:#dc2626;">-₹${printData.DiscountAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span></div>` : ''}
                  <div class="total-row grand-total">
                    <span class="label">Grand Total</span>
                    <span class="value">₹${(printData?.GrandTotal || printData?.TotalAmount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                  </div>
                </div>
              </div>
              
              <!-- Footer -->
              <div class="footer-section">
                <div class="terms">
                  <h4>Terms & Conditions</h4>
                  <p>• Payment is due upon receipt of invoice</p>
                  <p>• Please retain this invoice for your records</p>
                </div>
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-label">Authorized Signature</div>
                </div>
              </div>
              
              <div class="print-footer">
                Thank you for choosing ${printData?.CompanyName || 'us'}! • Generated on ${new Date().toLocaleString('en-IN')}
              </div>
            </div>
          </body>
          </html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
          }, 300);
        } else {
          showBanner('Please allow popups to print', 'error');
        }
      } else {
        showBanner('Failed to load entry details for PDF generation', 'error');
      }
    } catch (error) {
      console.error('Error fetching entry details for PDF generation:', error);
      showBanner('Failed to load entry details for PDF generation', 'error');
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const downloadPrescriptionPDF = () => {
    if (!selectedEntry) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    // Header
    doc.setFillColor(102, 126, 234);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.text('Prescription Details', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Medical Record', pageWidth / 2, 25, { align: 'center' });

    yPosition = 45;

    // Patient Information Section
    doc.setFillColor(248, 250, 252);
    doc.rect(10, yPosition, pageWidth - 20, 40, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.rect(10, yPosition, pageWidth - 20, 40, 'S');

    doc.setTextColor(100, 116, 139);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');

    // Left column
    doc.text('PATIENT NAME', 15, yPosition + 8);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(selectedEntry.DoctorEntry?.PatientName || 'N/A', 15, yPosition + 14);

    doc.setTextColor(100, 116, 139);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('MRD NO', 15, yPosition + 23);
    doc.setTextColor(102, 126, 234);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(selectedEntry.DoctorEntry?.MRDNo || 'N/A', 15, yPosition + 29);

    // Middle column
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('DOCTOR', 80, yPosition + 8);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(selectedEntry.DoctorEntry?.DoctorName || 'N/A', 80, yPosition + 14);

    doc.setTextColor(100, 116, 139);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('PHONE', 80, yPosition + 23);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(selectedEntry.DoctorEntry?.ContactNumber1 || 'N/A', 80, yPosition + 29);

    // Right column
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('ENTRY DATE', 145, yPosition + 8);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(formatDate(selectedEntry.DoctorEntry?.DoctorEntryDate), 145, yPosition + 14);

    yPosition += 50;

    // Chief Complaint
    if (selectedEntry.DoctorEntry?.ChiefComplaint) {
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('CHIEF COMPLAINT', 15, yPosition);
      doc.setTextColor(55, 65, 81);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const complaintLines = doc.splitTextToSize(selectedEntry.DoctorEntry.ChiefComplaint, pageWidth - 30);
      doc.text(complaintLines, 15, yPosition + 6);
      yPosition += 6 + (complaintLines.length * 5) + 5;
    }

    // Diagnosis
    if (selectedEntry.DoctorEntry?.Diagnosis) {
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('DIAGNOSIS', 15, yPosition);
      doc.setTextColor(55, 65, 81);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const diagnosisLines = doc.splitTextToSize(selectedEntry.DoctorEntry.Diagnosis, pageWidth - 30);
      doc.text(diagnosisLines, 15, yPosition + 6);
      yPosition += 6 + (diagnosisLines.length * 5) + 10;
    }

    // Prescriptions Section
    if (selectedEntry.PrescriptionDetails && selectedEntry.PrescriptionDetails.length > 0) {
      // Check if we need a new page
      if (yPosition > pageHeight - 80) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFillColor(254, 243, 199);
      doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
      doc.setTextColor(146, 64, 14);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('PRESCRIPTIONS', 15, yPosition + 7);

      yPosition += 15;

      // Table Header
      doc.setFillColor(30, 41, 59);
      doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
      doc.setTextColor(241, 245, 249);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('MEDICINE', 15, yPosition + 7);
      doc.text('DOSAGE', 70, yPosition + 7);
      doc.text('FREQUENCY', 110, yPosition + 7);
      doc.text('DURATION', 150, yPosition + 7);

      yPosition += 10;

      // Table Rows
      selectedEntry.PrescriptionDetails.forEach((pres, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
        }

        // Alternate row colors
        if (index % 2 === 0) {
          doc.setFillColor(255, 255, 255);
        } else {
          doc.setFillColor(248, 250, 252);
        }
        doc.rect(10, yPosition, pageWidth - 20, 12, 'F');

        doc.setTextColor(15, 23, 42);
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(pres.MedicineName || 'N/A', 15, yPosition + 8);

        doc.setTextColor(71, 85, 105);
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text(pres.Dosage || '-', 70, yPosition + 8);
        doc.text(pres.Frequency || '-', 110, yPosition + 8);
        doc.text(pres.Duration || '-', 150, yPosition + 8);

        yPosition += 12;

        // Instructions (if any)
        if (pres.Instructions) {
          doc.setTextColor(100, 116, 139);
          doc.setFontSize(8);
          doc.setFont(undefined, 'italic');
          const instructionLines = doc.splitTextToSize(`Instructions: ${pres.Instructions}`, pageWidth - 30);
          doc.text(instructionLines, 15, yPosition + 4);
          yPosition += 4 + (instructionLines.length * 4);
        }
      });
    }

    // Footer
    const footerY = pageHeight - 15;
    doc.setDrawColor(226, 232, 240);
    doc.line(10, footerY - 5, pageWidth - 10, footerY - 5);
    doc.setTextColor(107, 114, 128);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 15, footerY);
    doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, pageWidth - 15, footerY, { align: 'right' });

    // Save the PDF
    const fileName = `Prescription_${selectedEntry.DoctorEntry?.PatientName || 'Patient'}_${selectedEntry.DoctorEntry?.MRDNo || 'MRD'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    showBanner('Prescription PDF downloaded successfully', 'success');
  };

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ padding: '10px', maxWidth: '100%' }}>
        {/* Banner Notification */}
        {banner.show && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            minWidth: '300px',
            maxWidth: '500px',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideInRight 0.4s ease-out',
            background: banner.type === 'success'
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : banner.type === 'error'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white'
          }}>
            <span style={{ fontSize: '24px' }}>
              {banner.type === 'success' ? '✅' : banner.type === 'error' ? '❌' : 'ℹ️'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                {banner.type === 'success' ? 'Success' : banner.type === 'error' ? 'Error' : 'Info'}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.95 }}>{banner.message}</div>
            </div>
            <button
              onClick={() => setBanner({ show: false, message: '', type: '' })}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                cursor: 'pointer',
                padding: '4px 8px',
                fontSize: '18px',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
        )}

        <style>{`
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(100px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes shake {
            0%, 100% { transform: rotate(0deg); }
            10%, 30%, 50%, 70%, 90% { transform: rotate(-10deg); }
            20%, 40%, 60%, 80% { transform: rotate(10deg); }
          }
          .table-row-hover {
            transition: all 0.3s ease;
          }
          .table-row-hover:hover {
            background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%) !important;
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
          }
        `}</style>

        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          marginTop: '-30px'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '8px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>🏥</span>
              <div>
                <h1 style={{ fontSize: '15px', fontWeight: '700', color: 'white', margin: 0, lineHeight: '1.2' }}>
                  Doctor Entries
                </h1>
                <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)', margin: '2px 0 0 0', lineHeight: '1.3' }}>
                  View and manage all patient examination records
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Search Bar */}
              <input
                type="text"
                placeholder="🔍 Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '5px 10px',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  outline: 'none',
                  background: 'rgba(255,255,255,0.95)',
                  color: '#374151',
                  fontWeight: '500',
                  height: '28px',
                  width: '200px',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.boxShadow = '0 0 0 2px rgba(255,255,255,0.3)';
                }}
                onBlur={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.95)';
                  e.target.style.boxShadow = 'none';
                }}
              />

              {/* Date Filters */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    if (e.target.value && toDate) {
                      // Auto-fetch when both dates are set
                      setTimeout(() => {
                        if (new Date(e.target.value) <= new Date(toDate)) {
                          fetchDoctorEntries();
                        }
                      }, 100);
                    }
                  }}
                  style={{
                    padding: '5px 8px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    outline: 'none',
                    background: 'rgba(255,255,255,0.95)',
                    color: '#374151',
                    fontWeight: '600',
                    height: '28px',
                    width: '130px'
                  }}
                />
                <span style={{ color: 'white', fontSize: '12px', fontWeight: '600' }}>to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    if (fromDate && e.target.value) {
                      // Auto-fetch when both dates are set
                      setTimeout(() => {
                        if (new Date(fromDate) <= new Date(e.target.value)) {
                          fetchDoctorEntries();
                        }
                      }, 100);
                    }
                  }}
                  style={{
                    padding: '5px 8px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    outline: 'none',
                    background: 'rgba(255,255,255,0.95)',
                    color: '#374151',
                    fontWeight: '600',
                    height: '28px',
                    width: '130px'
                  }}
                />
              </div>

              {/* Refresh Button */}
              <Tooltip title="Refresh data" arrow>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  style={{
                    padding: '0 12px',
                    background: loading
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(255,255,255,0.95)',
                    color: loading ? 'rgba(255,255,255,0.7)' : '#10b981',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.3s ease',
                    height: '28px',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 255, 255, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ fontSize: '12px' }}>🔄</span>
                  Refresh
                </button>
              </Tooltip>

              {/* New Entry Button */}
              {/* <button
                onClick={() => history.push('/DoctorEntryCreate')}
                style={{
                  padding: '0 12px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  color: '#667eea',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  height: '28px',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <span style={{ fontSize: '12px' }}>➕</span>
                New Entry
              </button>*/}
            </div>
          </div>

          {/* Table */}
          <div style={{ padding: '12px 16px', minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                flex: 1
              }}>
                <CircularProgress style={{ color: '#667eea', width: '48px', height: '48px' }} />
                <p style={{ marginTop: '20px', color: '#6b7280', fontSize: '14px', fontWeight: '600' }}>Loading doctor entries...</p>
              </div>
            ) : filteredEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
                  No Doctor Entries Found
                </h3>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                  {searchQuery ? 'Try adjusting your search criteria' : 'Create your first doctor entry to get started'}
                </p>
              </div>
            ) : (
              <TableContainer component={Paper} style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}>
                <Table>
                  <TableHead>
                    <TableRow style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Entry Date</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Patient Info</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Doctor</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Chief Complaint</TableCell>
                      <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '10px', padding: '6px 10px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEntries.map((entry, index) => (
                      <TableRow
                        key={entry.Id}
                        className="table-row-hover"
                        style={{
                          background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                          animation: 'fadeIn 0.5s ease-out',
                          animationDelay: `${index * 0.05}s`,
                          animationFillMode: 'both'
                        }}
                      >
                        <TableCell style={{ padding: '6px 10px', fontSize: '11px', fontWeight: '600', color: '#667eea' }}>
                          {formatDate(entry.DoctorEntryDate)}
                        </TableCell>
                        <TableCell style={{ padding: '6px 10px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>
                            {entry.PatientName || 'N/A'}
                          </div>
                          <div style={{ fontSize: '10px', color: '#64748b' }}>
                            MRD: {entry.MRDNo || 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell style={{ padding: '6px 10px', fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                          {entry.DoctorName || 'N/A'}
                        </TableCell>
                        <TableCell style={{ padding: '6px 10px', fontSize: '11px', color: '#374151', maxWidth: '300px' }}>
                          {entry.ChiefComplaint ? (
                            entry.ChiefComplaint.length > 80
                              ? entry.ChiefComplaint.substring(0, 80) + '...'
                              : entry.ChiefComplaint
                          ) : 'N/A'}
                        </TableCell>

                        <TableCell style={{ padding: '6px 10px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <Tooltip title="Print Bill Details" arrow>
                              <button
                                onClick={() => handlePrint(entry.Id)}
                                style={{
                                  background: 'linear-gradient(135deg, #baedc2ff 0%, #bbf7c4ff 100%)',
                                  border: '1px solid #7df08eff',
                                  borderRadius: '6px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: 'rgba(7, 72, 36, 1)',
                                  transition: 'all 0.3s ease',
                                  height: '26px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(29, 78, 216, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                📇Print
                              </button>
                            </Tooltip>
                            <Tooltip title="View Details" arrow>
                              <button
                                onClick={() => handleView(entry.Id)}
                                style={{
                                  background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                                  border: '1px solid #a5b4fc',
                                  borderRadius: '6px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: '#4f46e5',
                                  transition: 'all 0.3s ease',
                                  height: '26px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(79, 70, 229, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                👁️ View
                              </button>
                            </Tooltip>
                            <Tooltip title="Edit Entry" arrow>
                              <button
                                onClick={() => handleEdit(entry.Id)}
                                style={{
                                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                  border: '1px solid #fcd34d',
                                  borderRadius: '6px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: '#92400e',
                                  transition: 'all 0.3s ease',
                                  height: '26px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(245, 158, 11, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                ✏️ Edit
                              </button>
                            </Tooltip>
                            <Tooltip title="Delete Entry" arrow>
                              <button
                                onClick={() => handleDelete(entry)}
                                style={{
                                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                  border: '1px solid #fca5a5',
                                  borderRadius: '6px',
                                  padding: '4px 8px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: '#dc2626',
                                  transition: 'all 0.3s ease',
                                  height: '26px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                🗑️ Delete
                              </button>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>

          {/* Footer Stats */}
          {!loading && filteredEntries.length > 0 && (
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>
                Showing {filteredEntries.length} of {doctorEntries.length} entries
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  <span style={{ fontWeight: '700', color: '#667eea' }}>{doctorEntries.length}</span> Total Entries
                </div>
              </div>
            </div>
          )}
        </div>

        {/* View Modal */}
        {viewModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '1200px',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'scaleIn 0.3s ease-out'
            }}>
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>👁️</span>
                  <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'white', margin: 0 }}>
                      Doctor Entry Details
                    </h2>
                    <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px', margin: '4px 0 0 0' }}>
                      View complete patient examination record
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <Tooltip title="Download Prescription PDF" arrow>
                    <button
                      onClick={downloadPrescriptionPDF}
                      disabled={!selectedEntry || loadingDetails}
                      style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        cursor: (!selectedEntry || loadingDetails) ? 'not-allowed' : 'pointer',
                        color: '#667eea',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.3s ease',
                        opacity: (!selectedEntry || loadingDetails) ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (selectedEntry && !loadingDetails) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(255, 255, 255, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>📄</span>
                      Download PDF
                    </button>
                  </Tooltip>
                  <button
                    onClick={closeViewModal}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      cursor: 'pointer',
                      color: 'white',
                      fontSize: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                    onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div style={{ maxHeight: 'calc(90vh - 80px)', overflowY: 'auto', padding: '16px' }}>
                {loadingDetails ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '300px'
                  }}>
                    <CircularProgress style={{ color: '#667eea', width: '48px', height: '48px' }} />
                    <p style={{ marginTop: '20px', color: '#6b7280', fontSize: '14px', fontWeight: '600' }}>
                      Loading entry details...
                    </p>
                  </div>
                ) : selectedEntry ? (
                  <div>
                    {/* Patient & Doctor Info */}
                    <div style={{
                      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px 16px', alignItems: 'start' }}>
                        <div>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Patient Name
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a' }}>
                            {selectedEntry.DoctorEntry?.PatientName || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            MRD No
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#667eea' }}>
                            {selectedEntry.DoctorEntry?.MRDNo || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Phone
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#0f172a' }}>
                            {selectedEntry.DoctorEntry?.ContactNumber1 || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Doctor
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a' }}>
                            {selectedEntry.DoctorEntry?.DoctorName || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Entry Date
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#0f172a' }}>
                            {formatDate(selectedEntry.DoctorEntry?.DoctorEntryDate)}
                          </div>
                        </div>
                        <div style={{ gridColumn: 'span 3' }}>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Chief Complaint
                          </div>
                          <div style={{ fontSize: '11px', color: '#374151', lineHeight: '1.4' }}>
                            {selectedEntry.DoctorEntry?.ChiefComplaint || 'N/A'}
                          </div>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <div style={{ fontSize: '9px', fontWeight: '700', color: '#64748b', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Diagnosis
                          </div>
                          <div style={{ fontSize: '11px', color: '#374151', lineHeight: '1.4' }}>
                            {selectedEntry.DoctorEntry?.Diagnosis || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Observations */}
                    {selectedEntry.ObservationDetails && selectedEntry.ObservationDetails.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#667eea',
                          margin: '0 0 8px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 10px',
                          background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                          borderRadius: '6px',
                          border: '1px solid #c7d2fe'
                        }}>
                          <span style={{ fontSize: '16px' }}>🩺</span>
                          Observations ({selectedEntry.ObservationDetails.length})
                        </div>
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid #e2e8f0'
                        }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#1e293b' }}>
                                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</th>
                                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Value</th>
                                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Unit</th>
                                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedEntry.ObservationDetails.map((obs, index) => (
                                <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                                  <td style={{ padding: '8px 10px', fontSize: '11px', fontWeight: '600', color: '#475569' }}>{obs.ObservationType}</td>
                                  <td style={{ padding: '8px 10px', fontSize: '12px', fontWeight: '700', color: '#0f172a' }}>{obs.ObservationValue}</td>
                                  <td style={{ padding: '8px 10px', fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>{obs.Unit || '-'}</td>
                                  <td style={{ padding: '8px 10px', fontSize: '11px', color: '#64748b' }}>{obs.Remarks || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Prescriptions */}
                    {selectedEntry.PrescriptionDetails && selectedEntry.PrescriptionDetails.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#667eea',
                          margin: '0 0 8px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 10px',
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          borderRadius: '6px',
                          border: '1px solid #fcd34d'
                        }}>
                          <span style={{ fontSize: '16px' }}>💊</span>
                          Prescriptions ({selectedEntry.PrescriptionDetails.length})
                        </div>
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid #e2e8f0'
                        }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#1e293b' }}>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Medicine</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Dosage</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Frequency</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Duration</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Instructions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedEntry.PrescriptionDetails.map((pres, index) => (
                                <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                                  <td style={{ padding: '10px', fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{pres.MedicineName}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>{pres.Dosage}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>{pres.Frequency || '-'}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>{pres.Duration || '-'}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>{pres.Instructions || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Treatments */}
                    {selectedEntry.TreatmentDetails && selectedEntry.TreatmentDetails.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#667eea',
                          margin: '0 0 8px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 10px',
                          background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                          borderRadius: '6px',
                          border: '1px solid #6ee7b7'
                        }}>
                          <span style={{ fontSize: '16px' }}>🏥</span>
                          Treatments ({selectedEntry.TreatmentDetails.length})
                        </div>
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid #e2e8f0'
                        }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#1e293b' }}>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Treatment</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Session</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Description</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Start Date</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>End Date</th>
                                <th style={{ padding: '10px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Rate</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedEntry.TreatmentDetails.map((treat, index) => (
                                <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                                  <td style={{ padding: '10px', fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{treat.TreatmentName}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', color: '#8b5cf6', fontWeight: '600' }}>{treat.SessionName || '-'}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>{treat.Description || '-'}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>{formatDate(treat.StartDate)}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>{formatDate(treat.EndDate)}</td>
                                  <td style={{ padding: '10px', fontSize: '13px', textAlign: 'right', color: '#059669', fontWeight: '700' }}>{treat.Rate || '0'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Questionnaire */}
                    {selectedEntry.TreatmentQuestionier && selectedEntry.TreatmentQuestionier.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#667eea',
                          margin: '0 0 8px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 10px',
                          background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                          borderRadius: '6px',
                          border: '1px solid #f9a8d4'
                        }}>
                          <span style={{ fontSize: '16px' }}>❓</span>
                          Questions & Answers ({selectedEntry.TreatmentQuestionier.length})
                        </div>
                        <div style={{
                          background: '#f8fafc',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid #e2e8f0'
                        }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#1e293b' }}>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Question ID</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Question</th>
                                <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#f1f5f9', textTransform: 'uppercase' }}>Answer</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedEntry.TreatmentQuestionier.map((q, index) => (
                                <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                                  <td style={{ padding: '10px', fontSize: '12px', fontWeight: '700', color: '#667eea' }}>
                                    {q.QuestioniertId ? `#${q.QuestioniertId}` : 'N/A'}
                                  </td>
                                  <td style={{ padding: '10px', fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{q.Question}</td>
                                  <td style={{ padding: '10px', fontSize: '12px', fontWeight: '600', color: '#475569' }}>{q.Answer}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>No data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '20px',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '500px',
              overflow: 'hidden',
              boxShadow: '0 25px 70px rgba(220, 38, 38, 0.4)',
              animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              border: '2px solid #fecaca'
            }}>
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  animation: 'shake 0.5s ease-in-out'
                }}>
                  ⚠️
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'white', margin: 0 }}>
                    Confirm Deletion
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', margin: '4px 0 0 0' }}>
                    This action cannot be undone
                  </p>
                </div>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '24px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid #fecaca',
                  marginBottom: '20px'
                }}>
                  <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 12px 0', fontWeight: '600' }}>
                    Are you sure you want to delete this doctor entry?
                  </p>
                  {entryToDelete && (
                    <div style={{
                      background: 'white',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #fca5a5'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>Patient:</span>
                          <span style={{ fontSize: '12px', color: '#0f172a', fontWeight: '700' }}>
                            {entryToDelete.PatientName || 'N/A'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>MRD No:</span>
                          <span style={{ fontSize: '12px', color: '#667eea', fontWeight: '700' }}>
                            {entryToDelete.MRDNo || 'N/A'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>Doctor:</span>
                          <span style={{ fontSize: '12px', color: '#0f172a', fontWeight: '700' }}>
                            {entryToDelete.DoctorName || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={cancelDelete}
                    disabled={deleting}
                    style={{
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: deleting ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!deleting) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={deleting}
                    style={{
                      padding: '10px 20px',
                      background: deleting
                        ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                        : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '120px',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (!deleting) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {deleting ? (
                      <>
                        <CircularProgress size={16} style={{ color: 'white' }} />
                        <span>Deleting...</span>
                      </>
                    ) : (
                      <>
                        <span>🗑️</span>
                        <span>Delete Entry</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Fade>
  );
};

export default DoctorEntryIndex;
